import { useState, useEffect, useRef } from 'react'
import EditorIcon from '@/shared/icons/EditorIcon-20x20.svg?react'

import { useUpdateMarkingFormData } from '@/shared/api'
import { apiQueryKey } from '@/shared/api'
import { ORDMarkingFormData } from '@synapex/prisma/client'
import { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'
import { useQueryClient } from '@tanstack/react-query'
import {
    Tooltip,
    TooltipProvider,
    TooltipTrigger,
    TooltipContent,
} from '@/shared/ui/tooltip'
import { cn } from '@/true-fsd/shared/utils/css'

import classes from './MarkingSettings.module.scss'
import { MarkingMediaTextBlock } from './MarkingMediaTextBlock'
import { MarkingPhotoColor } from '../utils'
import { Checkbox2, Checkbox2Props } from '@/true-fsd/shared/ui/Checkbox2'
import { MarkingEditorModal } from '../../MarkingEditor/MarkingEditorModal'
import type { ICreativeWithMarkableFile } from '../../MarkingEditor/types'
import { migrateOldMarkingData } from '../../MarkingEditor/utils'

const useUpdateMarkingSettings = (wwld: WorkspaceWithLinkDescriptor) => {
    const client = useQueryClient()

    const updateMarkingFormData = useUpdateMarkingFormData(wwld, {
        onMutate: async (updateMarkingFormDataDto) => {
            const queryKey = apiQueryKey.markingFormData(wwld)

            await client.cancelQueries({
                queryKey,
            })

            const ordMarkingFormDataBackup: ORDMarkingFormData | undefined =
                client.getQueryData(queryKey) ?? undefined

            client.setQueryData<ORDMarkingFormData>(
                queryKey,
                (currentMarkingForm) => {
                    if (!currentMarkingForm) {
                        return undefined
                    }

                    return {
                        ...currentMarkingForm,
                        ...updateMarkingFormDataDto,
                    }
                }
            )

            return { ordMarkingFormDataBackup }
        },
        onError: (error, _, context) => {
            console.error(error)

            if (context && context.ordMarkingFormDataBackup) {
                client.setQueryData(
                    apiQueryKey.markingFormData(wwld),
                    context.ordMarkingFormDataBackup
                )
            }
        },
    })

    const onCheckedChangeText: NonNullable<
        Checkbox2Props['onCheckedChange']
    > = (checked) => {
        updateMarkingFormData.mutate({
            mark_text: checked === true,
            mark_media: checked === true ? false : undefined, // Не меняем mark_media если снимаем чекбокс
        })
    }

    const onCheckedChangeMedia: NonNullable<
        Checkbox2Props['onCheckedChange']
    > = (checked) => {
        updateMarkingFormData.mutate({
            mark_text: checked === true ? false : undefined, // Не меняем mark_text если снимаем чекбокс
            mark_media: checked === true,
        })
    }

    const onChangeMediaText: (value: string) => void = (value) => {
        updateMarkingFormData.mutate({
            mark_media_text: value,
        } as Parameters<typeof updateMarkingFormData.mutate>[0])
    }

    return {
        onCheckedChangeText,
        onCheckedChangeMedia,
        onChangeMediaText,
    }
}

export type MarkingSettingsProps = {
    markText: NonNullable<Checkbox2Props['checked']>
    markMedia: NonNullable<Checkbox2Props['checked']>
    markMediaColor: MarkingPhotoColor
    markMediaText: ORDMarkingFormData['mark_media_text']
    /**
     * Креатив для передачи в редактор маркировки (опционально)
     */
    creative?: ICreativeWithMarkableFile | null
    /**
     * Данные маркировки для начальных значений редактора
     */
    markingFormData?: ORDMarkingFormData
    disabled?: boolean
    hasError?: boolean
}
export const MarkingSettings = ({
    markText,
    markMedia,
    markMediaColor,
    markMediaText,
    creative,
    markingFormData,
    disabled,
    hasError,
    ...wwld
}: MarkingSettingsProps & WorkspaceWithLinkDescriptor) => {
    const [isEditorOpen, setIsEditorOpen] = useState(false)
    const queryClient = useQueryClient()
    const prevCreativeIdRef = useRef(creative?.id)

    const hasMarkableMediaFile = Boolean(creative?.markableFile)

    const { onCheckedChangeMedia, onCheckedChangeText, onChangeMediaText } =
        useUpdateMarkingSettings(wwld)

    // Инвалидируем кэш при изменении креатива
    // Это необходимо, так как настройки редактора хранятся по creative_id
    useEffect(() => {
        const currentCreativeId = creative?.id
        if (
            currentCreativeId !== prevCreativeIdRef.current &&
            prevCreativeIdRef.current !== undefined
        ) {
            queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(wwld),
            })
        }
        prevCreativeIdRef.current = currentCreativeId
    }, [creative?.id, queryClient, wwld])

    // Подготовка начальных данных для редактора
    const initialEditorData = markingFormData
        ? migrateOldMarkingData(markingFormData)
        : undefined

    const checkboxColor = hasError ? 'red' : 'green'

    return (
        <div className={classes.root}>
            <div className={classes.title}>Настройка подписи</div>
            <div className={classes.content}>
                <label className={classes.labeledCheckbox}>
                    <Checkbox2
                        color={checkboxColor}
                        size={'large'}
                        checked={markText}
                        onCheckedChange={onCheckedChangeText}
                    />
                    <div className={classes.text}>
                        Подпись внизу
                        <br /> текста
                    </div>
                </label>
                {hasMarkableMediaFile && (
                    <label className={classes.labeledCheckbox}>
                        <Checkbox2
                            color={checkboxColor}
                            size={'large'}
                            checked={markMedia}
                            onCheckedChange={onCheckedChangeMedia}
                        />
                        <div className={classes.text}>
                            Подпись <br /> на медиа
                        </div>
                    </label>
                )}
                {hasMarkableMediaFile && markMedia && (
                    <TooltipProvider>
                        <Tooltip delayDuration={300}>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    className={classes.editorButton}
                                    onClick={() => setIsEditorOpen(true)}
                                    aria-label="Редактор подписи"
                                >
                                    <EditorIcon
                                        className={classes.editorIcon}
                                    />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent
                                side="top"
                                sideOffset={4}
                                alignOffset={7}
                                className={cn(
                                    classes.editorTooltip,
                                    'z-[60]'
                                )}
                            >
                                Редактор подписи
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}
            </div>

            <MarkingMediaTextBlock
                className={classes.mediaTextBlock}
                markMediaText={markMediaText}
                onSubmit={onChangeMediaText}
                disabled={disabled}
            />

            {hasMarkableMediaFile && (
                <MarkingEditorModal
                    {...wwld}
                    creative={creative}
                    initialData={initialEditorData}
                    open={isEditorOpen}
                    onOpenChange={setIsEditorOpen}
                />
            )}
        </div>
    )
}
